import React from 'react';

/**
 * SEOHead: lightweight meta tags + JSON-LD helper
 * Usage:
 * <SEOHead title="..." description="..." url="/community/123" keywords={["fbr","invoice"]} />
 */
export default function SEOHead({ title, description, url = '', keywords = [], image, schemaType = 'DiscussionForumPosting' }) {
  const fullUrl = typeof window !== 'undefined' ? `${window.location.origin}${url || window.location.pathname}` : url;
  const safeTitle = title?.slice(0, 60) || 'FBRSTAPP Community';
  const safeDesc = description?.slice(0, 160) || 'Ask questions and share tips about FBR Digital Invoicing.';
  const kw = Array.isArray(keywords) ? keywords.join(', ') : String(keywords || '');

  const baseSchema = {
    '@context': 'https://schema.org',
    headline: safeTitle,
    description: safeDesc,
    text: safeDesc,
    url: fullUrl,
    datePublished: new Date().toISOString(),
    author: {
      '@type': 'Person',
      name: 'FBRSTAPP Community'
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': fullUrl
    },
    publisher: {
      '@type': 'Organization',
      name: 'FBRSTAPP',
      url: 'https://fbrstapp.com',
      logo: {
        '@type': 'ImageObject',
        url: 'https://fbrstapp.com/logo.png'
      }
    },
    ...(image && { image: image }),
  };

  const jsonLd = {
    ...baseSchema,
    '@type': schemaType
  };

  return (
    <>
      {/* Basic SEO */}
      <title>{safeTitle}</title>
      <meta name="description" content={safeDesc} />
      {kw && <meta name="keywords" content={kw} />}

      {/* Open Graph */}
      <meta property="og:type" content="article" />
      <meta property="og:title" content={safeTitle} />
      <meta property="og:description" content={safeDesc} />
      {fullUrl && <meta property="og:url" content={fullUrl} />}
      {image && <meta property="og:image" content={image} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={safeTitle} />
      <meta name="twitter:description" content={safeDesc} />
      {image && <meta name="twitter:image" content={image} />}

      {/* JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <link rel="canonical" href={fullUrl} />
    </>
  );
}